import React, { useEffect, useRef, useState, useMemo } from "react";
import { MapContainer, GeoJSON } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "./Map.css";

/* ================= HELPERS ================= */

const normalize = (v) => {
  if (v === null || v === undefined) return "";
  return String(v).trim().toLowerCase().replace(/\s+/g, " ");
};

/* ================= COMPONENT ================= */

const Map = ({ filters }) => {
  const mapRef = useRef(null);

  const stateAbortRef = useRef(null);
  const districtAbortRef = useRef(null);

  const [stateGeo, setStateGeo] = useState(null);
  const [districtGeo, setDistrictGeo] = useState(null);

  const [stateData, setStateData] = useState([]);
  const [districtData, setDistrictData] = useState([]);

  const [showDistrictMap, setShowDistrictMap] = useState(false);
  const [selectedState, setSelectedState] = useState(null);

  const [loadingStateData, setLoadingStateData] = useState(false);
  const [loadingDistrictData, setLoadingDistrictData] = useState(false);

  const isLoading = loadingStateData || loadingDistrictData;

  /* ================= DATE API SWITCH ================= */

  const hasDateFilter =
Boolean(filters.fromMonth) && Boolean(filters.toMonth);


  const STATE_API = hasDateFilter
    ? "https://api-sales-insights.haqdarshak.com/state/month"
    : "https://api-sales-insights.haqdarshak.com/state";

  const DISTRICT_API = hasDateFilter
    ? "https://api-sales-insights.haqdarshak.com/district/month"
    : "https://api-sales-insights.haqdarshak.com/district";

  /* ================= COLORS ================= */

  const getColor = (value) =>
    value > 5000 ? "#5C0994" :
    value > 3000 ? "#7E1BB8" :
    value > 1500 ? "#9F2DDB" :
    value > 500  ? "#C66BFF" :
    value > 0    ? "#E2E2FF" :
                   "#F0E6FF";

  const getMetricValue = (obj) => {
    if (!obj) return 0;
    switch (filters.heatmapType) {
      case "DS": return obj.ds || 0;
      case "BR": return obj.br || 0;
      case "Benefit Value": return obj.bv || 0;
      default: return obj.opened || 0;
    }
  };

  /* ================= LOAD STATE GEO ================= */

  useEffect(() => {
    fetch("/india.json")
      .then(res => res.json())
      .then(data => {
        setStateGeo(data);
        if (mapRef.current) {
          mapRef.current.fitBounds(L.geoJSON(data).getBounds());
        }
      })
      .catch(console.error);
  }, []);

  /* ================= FETCH STATE DATA (RACE SAFE) ================= */

  useEffect(() => {

    // Abort previous request
    if (stateAbortRef.current) {
      stateAbortRef.current.abort();
    }

    const controller = new AbortController();
    stateAbortRef.current = controller;

    setLoadingStateData(true);
    setStateData([]);
    setDistrictData([]);
    setShowDistrictMap(false);
    setSelectedState(null);

    fetch(STATE_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(filters),
      signal: controller.signal
    })
      .then(res => res.json())
      .then(data => {
        setStateData(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        if (err.name !== "AbortError") {
          setStateData([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoadingStateData(false);
        }
      });

    return () => controller.abort();

  }, [filters, STATE_API]);

  /* ================= STATE MAP ================= */

  const stateMap = useMemo(() => {
    const acc = {};
    stateData.forEach(s => {
      if (s?.State) acc[normalize(s.State)] = s;
    });
    return acc;
  }, [stateData]);

  /* ================= DISTRICT MAP ================= */

  const districtMap = useMemo(() => {
    const acc = {};
    districtData.forEach(d => {
      if (d?.State && d?.District) {
        acc[`${normalize(d.State)}__${normalize(d.District)}`] = d;
      }
    });
    return acc;
  }, [districtData]);

  /* ================= LOAD DISTRICTS (RACE SAFE) ================= */

  const loadDistricts = (stateName) => {

    if (districtAbortRef.current) {
      districtAbortRef.current.abort();
    }

    const controller = new AbortController();
    districtAbortRef.current = controller;

    setSelectedState(stateName);
    setShowDistrictMap(true);
    setDistrictData([]);
    setLoadingDistrictData(true);

    fetch(DISTRICT_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...filters, regionState: [stateName] }),
      signal: controller.signal
    })
      .then(res => res.json())
      .then(data => {
        setDistrictData(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        if (err.name !== "AbortError") {
          setDistrictData([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoadingDistrictData(false);
        }
      });

    fetch("/india_district.geojson")
      .then(res => res.json())
      .then(geo => {
        const filtered = geo.features.filter(
          f => normalize(f?.properties?.st_nm) === normalize(stateName)
        );
        setDistrictGeo({ type: "FeatureCollection", features: filtered });

        if (mapRef.current && filtered.length) {
          mapRef.current.fitBounds(L.geoJSON(filtered).getBounds());
        }
      });
  };

  /* ================= STYLES ================= */

  const stateStyle = (feature) => ({
    fillColor: getColor(
      getMetricValue(stateMap[normalize(feature?.properties?.st_nm)])
    ),
    weight: 1.2,
    color: "#fff",
    fillOpacity: 0.85
  });

  const districtStyle = (feature) => ({
    fillColor: getColor(
      getMetricValue(
        districtMap[
          `${normalize(feature?.properties?.st_nm)}__${normalize(
            feature?.properties?.NAME_2
          )}`
        ]
      )
    ),
    weight: 1,
    color: "#fff",
    fillOpacity: 0.85
  });

  /* ================= POPUPS ================= */

  const onEachState = (feature, layer) => {
    const stateName = feature?.properties?.st_nm;
    const s = stateMap[normalize(stateName)];
    const btnId = `btn-${normalize(stateName)}`;

    layer.bindPopup(`
      <b>${stateName}</b><br/>
      Applications Open: ${s?.opened || 0}<br/>
      Docket Submit: ${s?.ds || 0}<br/>
      Benefits Received: ${s?.br || 0}<br/>
      Benefit Value: ₹${s?.bv || 0}<br/><br/>
      <button id="${btnId}">Show Districts</button>
    `);

    layer.on("popupopen", () => {
      document.getElementById(btnId)?.addEventListener("click", () =>
        loadDistricts(stateName)
      );
    });
  };

  const onEachDistrict = (feature, layer) => {
    const d =
      districtMap[
        `${normalize(feature?.properties?.st_nm)}__${normalize(
          feature?.properties?.NAME_2
        )}`
      ];

    layer.bindPopup(`
      <b>${feature?.properties?.NAME_2}</b><br/>
      Applications Open: ${d?.opened || 0}<br/>
      DS: ${d?.ds || 0}<br/>
      BR: ${d?.br || 0}<br/>
      Benefit Value: ₹${d?.bv || 0}
    `);
  };

  /* ================= BACK ================= */

  const handleBack = () => {
    setShowDistrictMap(false);
    setSelectedState(null);
    setDistrictGeo(null);
    setDistrictData([]);

    if (mapRef.current && stateGeo) {
      mapRef.current.fitBounds(L.geoJSON(stateGeo).getBounds());
    }
  };

  /* ================= RENDER ================= */

  return (
    <div className="map">
      {isLoading && (
        <div className="map-loader">
          <div className="spinner" />
          <p>Loading map data...</p>
        </div>
      )}

      <MapContainer
        zoom={5}
        center={[22.9, 82.6]}
        style={{ height: "100%", width: "100%" }}
        whenCreated={(map) => (mapRef.current = map)}
        scrollWheelZoom={false}
      >
        {!isLoading && !showDistrictMap && stateGeo && (
          <GeoJSON
            key={JSON.stringify(stateData)}
            data={stateGeo}
            style={stateStyle}
            onEachFeature={onEachState}
          />
        )}

        {!isLoading && showDistrictMap && districtGeo && (
          <>
            <GeoJSON
              key={JSON.stringify(districtData)}
              data={districtGeo}
              style={districtStyle}
              onEachFeature={onEachDistrict}
            />
            <button className="back-button" onClick={handleBack}>
              Back to State Map
            </button>
          </>
        )}
      </MapContainer>
    </div>
  );
};

export default Map;
